import { FC, useMemo } from 'react';
import urlJoin from 'url-join';
import QRCode from 'react-qr-code';
import { differenceInHours, parseISO } from 'date-fns';

import { Button } from '@/Components/Button';
import { CloseSvg } from '@/Components/Icons/CloseSvg';
import { ExclamationSvg } from '@/Components/Icons/ExclamationSvg';
import { LogoSvg } from '@/Components/Icons/LogoSvg';
import { ModalView } from '@/Components/ModalView';
import { FlowStatus } from '@/types/flow.type';
import { AppModalProps } from '@/types/modal.type';
import { FruitLemonSvg } from '@/Components/Icons/FruitLemonSvg';

type FlowPreviewModalProps = AppModalProps<{
    flowPage: string;
    flowCode: string;
    flowName: string;
    flowStatus: FlowStatus;
    workspaceUrl: string;
    isCodeInstalled: boolean;
    installationVerifiedAt: string | null;
}>;

const NEVER_INSTALLED_VIEW_MODE = 'neverInstalled';
const INSTALL_ISSUE_VIEW_MODE = 'installIssue';
const VERIFIED_VIEW_MODE = 'verified';

const VIEW_MODES = [
    NEVER_INSTALLED_VIEW_MODE,
    INSTALL_ISSUE_VIEW_MODE,
    VERIFIED_VIEW_MODE,
] as const;

type FlowPreviewModalViewMode = (typeof VIEW_MODES)[number];

export const FlowPreviewModal: FC<FlowPreviewModalProps> = ({
    payload,
    onClose,
}) => {
    const flowName = payload?.flowName;
    const flowPage = payload?.flowPage;
    const flowCode = payload?.flowCode;
    const flowStatus = payload?.flowStatus;
    const workspaceUrl = payload?.workspaceUrl;
    const isCodeInstalled = payload?.isCodeInstalled ?? false;
    const installationVerifiedAt = payload?.installationVerifiedAt;

    const previewUrl = urlJoin(workspaceUrl || '', flowPage).toString();
    const previewUrlWithParams = useMemo(
        () =>
            urlJoin(
                previewUrl,
                `?reelflow_preview=1&reelflow_flow=${flowCode}`,
            ).toString(),
        [flowCode, previewUrl],
    );

    const viewMode: FlowPreviewModalViewMode = useMemo(() => {
        let lastInstallationVerifiedAtHoursAgo = null;

        if (installationVerifiedAt) {
            try {
                const verifiedDate = parseISO(installationVerifiedAt);

                lastInstallationVerifiedAtHoursAgo = differenceInHours(
                    new Date(),
                    verifiedDate,
                );
            } catch (ex) {}
        }

        if (
            lastInstallationVerifiedAtHoursAgo &&
            lastInstallationVerifiedAtHoursAgo > 72
        ) {
            return INSTALL_ISSUE_VIEW_MODE;
        } else if (isCodeInstalled) {
            return VERIFIED_VIEW_MODE;
        }

        return NEVER_INSTALLED_VIEW_MODE;
    }, []);

    const settingsPageUrl = route('settings.installation', {
        redirect_to: window?.location?.href,
        redirect_label: encodeURIComponent(flowName),
    });

    return (
        <ModalView
            maxWidth="md"
            onClose={onClose}
            contentContainerClassName="py-4 text-brand-ocean">
            <div className="flex flex-row items-center justify-end">
                <button
                    title="Close"
                    aria-label="Close"
                    className="mx-4 p-2 transition-all rounded-sm hover:cursor-pointer hover:bg-neutral-mid"
                    onClick={onClose}>
                    <CloseSvg className="[&_path]:stroke-1 [&_path]:stroke-brand-ocean" />
                </button>
            </div>
            {viewMode === NEVER_INSTALLED_VIEW_MODE && (
                <>
                    <div className="flex flex-row items-center justify-center mb-2">
                        <LogoSvg className="mb-8 -mr-1.5" />
                        <ExclamationSvg />
                    </div>
                    <div className="mx-6 text-center">
                        <p className="text-heading-four font-area-normal font-bold text-brand-ocean">
                            Add Install Code to website, to preview
                        </p>
                        <p className="mt-4 text-body-text-15 text-brand-ocean">
                            To preview Flows, you first need to add the <br />
                            Install Code to your website.
                        </p>
                    </div>
                    <div className="flex flex-row items-center justify-center mt-4 mb-2">
                        <Button
                            elementType="link"
                            href={settingsPageUrl}
                            variant="primary"
                            onClick={onClose}
                            className="h-9 flex items-center justify-center w-full mx-8 text-center">
                            Get the Install Code from Settings
                        </Button>
                    </div>
                </>
            )}
            {viewMode === INSTALL_ISSUE_VIEW_MODE && (
                <>
                    <div className="flex flex-row items-center justify-center mb-2">
                        <LogoSvg className="mb-6 -mr-2.5" />
                        <FruitLemonSvg />
                    </div>
                    <div className="mx-6 text-center">
                        <p className="text-heading-four font-area-normal font-bold text-brand-ocean">
                            Install Code issue may prevent preview
                        </p>
                        <p className="mt-4 text-body-text-15 text-brand-ocean">
                            It’s unclear if the Install Code on your website{' '}
                            <br />
                            is still working. Visit settings to verify it again.
                        </p>
                    </div>
                    <div className="flex flex-col items-center justify-center mt-4 mb-2 mx-8 gap-y-3">
                        <Button
                            elementType="link"
                            href={settingsPageUrl}
                            variant="primary"
                            onClick={onClose}
                            className="h-9 flex items-center justify-center w-full text-center">
                            Verify Install Code in Settings
                        </Button>
                        <Button
                            target="_blank"
                            variant="default"
                            elementType="a"
                            href={previewUrlWithParams}
                            onClick={onClose}
                            className="h-9 flex items-center justify-center w-full text-center">
                            Ignore and try preview...
                        </Button>
                    </div>
                </>
            )}
            {viewMode === VERIFIED_VIEW_MODE && (
                <>
                    <div className="flex flex-row items-center justify-center mb-2">
                        <LogoSvg className="mb-8 -mr-1.5" />
                        <ExclamationSvg />
                    </div>
                    <p className="text-heading-four font-area-normal font-bold text-brand-ocean text-center mx-4">
                        Preview Flow on your website
                    </p>
                    <div className="flex flex-row items-center justify-center mx-8 mt-4">
                        <p className="text-center text-body-text-15 break-words text-blue-500 !w-full">
                            {previewUrl}
                        </p>
                    </div>
                    <div className="mt-6 flex flex-row items-start justify-center text-body-text text-brand-ocean px-6">
                        <div className="bg-white p-1 rounded mr-4">
                            <QRCode
                                value={previewUrlWithParams}
                                size={82}
                                viewBox="0 0 128 128"
                            />
                        </div>
                        <ul className="text-body-text-15 text-left list-disc list-outside ml-2 pl-4 space-y-2">
                            <li>
                                Changes are not visible to visitors until
                                published
                            </li>
                            <li>
                                Activity will not count towards your performance
                                metrics
                            </li>
                            <li>
                                Scan the QR code to preview on a mobile device
                            </li>
                        </ul>
                    </div>
                    <div className="flex flex-row items-center justify-center mt-6 mb-2 mx-8">
                        <Button
                            elementType="a"
                            target="_blank"
                            href={previewUrlWithParams}
                            variant="primary"
                            onClick={onClose}
                            className="h-9 flex items-center justify-center w-full text-center">
                            Preview on website (opens in new tab)
                        </Button>
                    </div>
                </>
            )}
        </ModalView>
    );
};
